import { useEffect, useMemo, useState } from "react";
import { Link } from "react-router-dom";
import api, { errMsg } from "@/lib/api";
import { useLang } from "@/lib/i18n";
import { toast } from "sonner";
import { Button } from "@/components/ui/button";
import { Label } from "@/components/ui/label";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "@/components/ui/select";
import { Sparkles, Crown, Star, Zap, CheckCircle2, MapPin, Globe2, ArrowRight } from "lucide-react";

const TIERS = [
  { key: "featured", label: "Featured", price: 19, icon: Sparkles, color: "text-primary border-primary/30 bg-primary/5",
    perks: { it: ["Posizionamento boost nella directory", "Badge 'In evidenza'", "Marker prominente sulla mappa"],
             en: ["Boost in directory ranking", "'Featured' badge", "Prominent map marker"] } },
  { key: "premium",  label: "Premium",  price: 49, icon: Star,     color: "text-sage border-sage/30 bg-sage/5",
    perks: { it: ["Tutto di Featured", "3x boost ranking", "Sponsorizzazione in homepage rotativa"],
             en: ["Everything in Featured", "3x boost ranking", "Featured on homepage rotator"] } },
  { key: "top",      label: "Top",      price: 99, icon: Crown,    color: "text-primary border-primary bg-primary/10",
    perks: { it: ["Tutto di Premium", "8x boost ranking (in cima)", "Copertina hero città", "Priorità supporto"],
             en: ["Everything in Premium", "8x boost ranking (top)", "City hero cover", "Priority support"] } },
];

export default function BoostCard({ compact = false }) {
  const { lang } = useLang();
  const IT = lang === "it";
  const [boost, setBoost] = useState(null);
  const [cities, setCities] = useState([]);
  const [loading, setLoading] = useState(null);
  const [scope, setScope] = useState("global"); // global | city
  const [city, setCity] = useState("");

  const load = () => { api.get("/boost/me").then((r) => setBoost(r.data)).catch(() => {}); };
  useEffect(() => {
    load();
    api.get("/directory/facets").then((r) => setCities(r.data.cities || [])).catch(() => {});
  }, []);

  const cityMult = boost?.city_price_multiplier ?? 0.6;
  const tierPrice = (base) => scope === "city" ? Math.round(base * cityMult) : base;

  const upgrade = async (tier) => {
    if (scope === "city" && !city) {
      toast.error(IT ? "Seleziona una città per il boost city-specific." : "Select a city for the city-specific boost.");
      return;
    }
    setLoading(tier);
    try {
      const payload = { tier, duration_days: 30 };
      if (scope === "city") payload.city = city;
      const r = await api.post("/boost/checkout", payload);
      window.location.href = r.data.url;
    } catch (e) { toast.error(errMsg(e)); setLoading(null); }
  };

  const active = boost?.active;
  const until = boost?.boost_until ? new Date(boost.boost_until).toLocaleDateString(lang === "it" ? "it-IT" : "en-US") : null;
  const cityBoosts = boost?.city_boosts || [];

  const activeTierIcon = useMemo(() => {
    if (!active) return null;
    if (boost.tier === "top") return <Crown className="w-10 h-10 text-primary opacity-80"/>;
    if (boost.tier === "premium") return <Star className="w-10 h-10 text-sage opacity-80"/>;
    return <Sparkles className="w-10 h-10 text-primary opacity-80"/>;
  }, [active, boost]);

  return (
    <div className="border border-primary/30 bg-gradient-to-br from-sand/40 to-primary/5 rounded-lg p-5 space-y-4" data-testid="boost-card">
      <div className="flex items-start justify-between gap-3">
        <div>
          <div className="uppercase tracking-[0.2em] text-[10px] text-primary font-semibold flex items-center gap-1.5">
            <Zap className="w-3 h-3"/>{IT ? "Visibilità premium" : "Premium visibility"}
          </div>
          <div className="font-display text-xl mt-1">
            {active
              ? (IT ? `Piano ${boost.tier} attivo` : `${boost.tier} plan active`)
              : (IT ? "Aumenta la visibilità della tua struttura" : "Boost your property visibility")}
          </div>
          <p className="text-xs text-muted-foreground mt-1 max-w-xl">
            {active
              ? (IT ? `Boost globale attivo fino al ${until}. Puoi aggiungere anche slot per città specifiche.` : `Global boost active until ${until}. You can also add city-specific slots.`)
              : (IT ? "Sali in cima ai risultati: boost globale su tutta la directory oppure slot city-specific più economico dedicato a una singola città." : "Rise to the top: global boost across the directory or a cheaper city-specific slot dedicated to a single city.")}
          </p>
        </div>
        {activeTierIcon}
      </div>

      {/* Active city boosts */}
      {cityBoosts.length > 0 && (
        <div className="flex flex-wrap gap-2" data-testid="active-city-boosts">
          {cityBoosts.map((cb) => (
            <div key={cb.city} className="border border-sage/40 bg-sage/10 text-sage-foreground rounded-full pl-1 pr-3 py-1 flex items-center gap-1.5 text-xs">
              <span className="bg-sage text-white rounded-full w-5 h-5 inline-flex items-center justify-center"><MapPin className="w-3 h-3"/></span>
              <span className="font-semibold">{cb.city}</span>
              <span className="uppercase tracking-widest text-[9px] opacity-70">{cb.tier}</span>
              <span className="text-[10px] opacity-60">· {IT ? "fino al" : "until"} {new Date(cb.until).toLocaleDateString(IT ? "it-IT" : "en-US")}</span>
            </div>
          ))}
        </div>
      )}

      {/* Scope selector (hidden in compact/sidebar mode) */}
      {!compact && (
      <div className="flex flex-wrap items-end gap-3 border-t border-border/60 pt-4" data-testid="boost-scope">
        <div>
          <Label className="text-[10px] uppercase tracking-widest text-muted-foreground">{IT ? "Ambito" : "Scope"}</Label>
          <div className="flex border border-border rounded-md overflow-hidden mt-1">
            <button type="button" onClick={() => setScope("global")}
              className={`px-3 py-2 text-xs flex items-center gap-1 ${scope === "global" ? "bg-primary text-primary-foreground" : "bg-card hover:bg-primary/5"}`}
              data-testid="boost-scope-global">
              <Globe2 className="w-3.5 h-3.5"/>{IT ? "Globale" : "Global"}
            </button>
            <button type="button" onClick={() => setScope("city")}
              className={`px-3 py-2 text-xs flex items-center gap-1 ${scope === "city" ? "bg-primary text-primary-foreground" : "bg-card hover:bg-primary/5"}`}
              data-testid="boost-scope-city">
              <MapPin className="w-3.5 h-3.5"/>{IT ? "Per città" : "Per city"}
            </button>
          </div>
        </div>
        {scope === "city" && (
          <div>
            <Label className="text-[10px] uppercase tracking-widest text-muted-foreground">{IT ? "Città" : "City"}</Label>
            <Select value={city} onValueChange={setCity}>
              <SelectTrigger className="w-56 mt-1" data-testid="boost-city-select">
                <SelectValue placeholder={IT ? "Seleziona città…" : "Select city…"}/>
              </SelectTrigger>
              <SelectContent>
                {cities.map((c) => <SelectItem key={c} value={c}>{c}</SelectItem>)}
              </SelectContent>
            </Select>
          </div>
        )}
        <div className="text-[11px] text-muted-foreground max-w-md">
          {scope === "city"
            ? (IT ? `Slot city-specific: appare in cima quando l'utente filtra per questa città. Prezzo scontato del ${Math.round((1-cityMult)*100)}%.` : `City-specific slot: appears at the top only for that city filter. ${Math.round((1-cityMult)*100)}% cheaper.`)
            : (IT ? "Boost globale: appare in cima in tutta la directory + homepage." : "Global boost: appears at the top across the whole directory + homepage.")}
        </div>
      </div>
      )}

      <div className={compact ? "grid grid-cols-1 gap-2" : "grid md:grid-cols-3 gap-3"}>
        {TIERS.map((tier) => {
          const Icon = tier.icon;
          const isCurrentGlobal = scope === "global" && boost?.tier === tier.key;
          const cityMatch = scope === "city" && cityBoosts.find((cb) => cb.city === city && cb.tier === tier.key);
          const isCurrent = !!(isCurrentGlobal || cityMatch);
          const price = tierPrice(tier.price);
          if (compact) {
            // Sidebar variant — one row per tier, no perks, button navigates to full page
            return (
              <Link
                key={tier.key} to={`/pricing#boost-${tier.key}`}
                data-testid={`boost-tier-${tier.key}`}
                className={`group flex items-center justify-between border rounded-lg p-3 lift ${isCurrent ? "border-primary bg-primary/10 ring-1 ring-primary/30" : "border-border bg-card hover:border-primary/40"}`}
              >
                <div className="flex items-center gap-2 min-w-0">
                  <Icon className="w-4 h-4 text-primary shrink-0"/>
                  <div className="min-w-0">
                    <div className="font-display text-base leading-tight">{tier.label}</div>
                    <div className="text-[10px] uppercase tracking-widest text-muted-foreground">€{tier.price} · 30gg</div>
                  </div>
                </div>
                {isCurrent
                  ? <span className="text-[10px] uppercase tracking-widest bg-primary text-primary-foreground rounded-full px-2 py-0.5">{IT ? "Attivo" : "Active"}</span>
                  : <span className="text-xs text-primary font-semibold flex items-center gap-1 group-hover:translate-x-0.5 transition-transform">{IT ? "Attiva" : "Activate"}<ArrowRight className="w-3 h-3"/></span>}
              </Link>
            );
          }
          return (
            <div key={tier.key} className={`border rounded-lg p-4 ${isCurrent ? "border-primary bg-primary/10 ring-2 ring-primary/30" : "border-border bg-card"} ${tier.color}`} data-testid={`boost-tier-${tier.key}`} id={`boost-${tier.key}`}>
              <div className="flex items-center gap-2 mb-1">
                <Icon className="w-4 h-4"/>
                <div className="font-display text-lg">{tier.label}</div>
                {isCurrent && <span className="ml-auto text-[10px] uppercase tracking-widest bg-primary text-primary-foreground rounded-full px-2 py-0.5">{IT ? "Attivo" : "Active"}</span>}
              </div>
              <div className="flex items-baseline gap-2">
                <div className="font-display text-3xl text-foreground">€{price}</div>
                {scope === "city" && <div className="text-xs text-muted-foreground line-through">€{tier.price}</div>}
              </div>
              <div className="text-[10px] uppercase tracking-widest text-muted-foreground mb-2">{IT ? "per 30 giorni" : "for 30 days"}</div>
              <ul className="text-xs space-y-1 mb-3">
                {tier.perks[IT ? "it" : "en"].map((p) => (
                  <li key={p} className="flex items-start gap-1.5">
                    <CheckCircle2 className="w-3 h-3 shrink-0 mt-0.5 opacity-70"/>{p}
                  </li>
                ))}
              </ul>
              <Button
                onClick={() => upgrade(tier.key)}
                disabled={loading === tier.key || isCurrent || (scope === "city" && !city)}
                size="sm" className="w-full lift"
                variant={isCurrent ? "outline" : "default"}
                data-testid={`boost-buy-${tier.key}`}
              >
                {loading === tier.key ? "…" : (isCurrent ? (IT ? "Attivo" : "Active") : (scope === "city" ? (IT ? `Attiva per ${city || "…"}` : `Activate for ${city || "…"}`) : (IT ? "Attiva" : "Activate")))}
              </Button>
            </div>
          );
        })}
      </div>
    </div>
  );
}
